import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import { ViewProps } from '../multiple-choice.constants';
import MultipleChoiceRadio from './multiple-choice-radio';
import MultipleChoiceCheck from './multiple-choice-check';
import {
  canShowOptionLetter,
  shouldDisableUnselected,
} from '../multiple-choice.utils';
import { MultipleChoiceLetter } from '../multiple-choice';
import { MultipleChoiceListText } from './multiple-choice-view-list.style';
import { MultipleChoiceItems } from '../multiple-choice.style';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { setComponentMultiAnswer } from 'approot/activity-builder/check-answer/check-answer.utils';
import { isSingleSelect } from '../logic/is-single-select';
import { ACTIVITIES_ANSWER_MODE_FREE } from 'global/constants';

const MultipleChoiceViewList = ({
  id,
  sectionId,
  properties,
  onSelectOption,
  showAnswer,
}: ViewProps & {
  onSelectOption?: (answerRefs: string[]) => void;
  showAnswer: boolean;
}) => {
  const { multipleChoice } = properties;

  const sortedOptions = useMemo(
    () => sortBy(multipleChoice.options, 'sort'),
    [multipleChoice]
  );

  const { checkAnswerData, currentAnswerData } = useCheckAnswer({
    componentId: id,
  });

  const isFreeAnswer =
    multipleChoice.answerMode === ACTIVITIES_ANSWER_MODE_FREE;
  const canShowCorrectOrIncorrect = !isFreeAnswer && showAnswer === true;

  const hasAnswered = currentAnswerData?.hasChecked;
  const isOneOptionCorrect = useMemo(
    () =>
      isSingleSelect({
        answerMode: multipleChoice.answerMode,
        isFreeAnswerMultiSelect: multipleChoice.isFreeAnswerMultiSelect,
        options: multipleChoice.options,
      }),
    [multipleChoice]
  );

  const selectedAnswers = useMemo(
    () => currentAnswerData?.multiAnswer?.answerRefs || [],
    [currentAnswerData]
  );

  const shouldDisableUnselectedOptions = useMemo(
    () =>
      shouldDisableUnselected({
        options: sortedOptions,
        selectedAnswers,
        isOneOptionCorrect,
        isFreeAnswer,
      }),
    [sortedOptions, selectedAnswers, isOneOptionCorrect, isFreeAnswer]
  );

  return (
    <MultipleChoiceItems role={isOneOptionCorrect ? 'radiogroup' : 'checkbox'}>
      {sortedOptions.map((option, index) => {
        const optionSelected = selectedAnswers.includes(option.itemId || '1');

        const showCorrect =
          canShowCorrectOrIncorrect &&
          optionSelected &&
          option.correctAnswerBoolean === true;
        const showIncorrect =
          canShowCorrectOrIncorrect &&
          optionSelected &&
          option.correctAnswerBoolean === false;

        const isDisabled =
          (hasAnswered && !optionSelected) ||
          (shouldDisableUnselectedOptions && !optionSelected);
        const content = (
          <>
            {canShowOptionLetter(
              properties.multipleChoice.showOptionLetter
            ) && (
              <MultipleChoiceLetter
                data-selector="multiple-choice-letter"
                index={index}
              />
            )}
            <MultipleChoiceListText>{option.text}</MultipleChoiceListText>
          </>
        );

        return isOneOptionCorrect ? (
          <MultipleChoiceRadio
            key={index}
            id={`${onSelectOption ? 'modal-' : ''}${id}-${index}`}
            name={`${onSelectOption ? 'modal-' : ''}${id}`}
            label={content}
            checked={optionSelected}
            disabled={hasAnswered && !optionSelected}
            data-check-answer-element={true}
            isCorrect={hasAnswered ? showCorrect : undefined}
            isIncorrect={hasAnswered ? showIncorrect : undefined}
            isSelected={hasAnswered && isFreeAnswer}
            onClick={() => {
              let newAnswerRefs = selectedAnswers;
              if (option.itemId && !hasAnswered) {
                newAnswerRefs = [option.itemId];
                setComponentMultiAnswer({
                  sectionId,
                  componentId: id,
                  answers: newAnswerRefs,
                  checkAnswerData,
                });
              }
              onSelectOption?.(newAnswerRefs);
            }}
          />
        ) : (
          <MultipleChoiceCheck
            key={index}
            label={content}
            checked={optionSelected}
            data-check-answer-element={true}
            disabled={isDisabled}
            isCorrect={
              hasAnswered && canShowCorrectOrIncorrect ? showCorrect : undefined
            }
            isIncorrect={
              hasAnswered && canShowCorrectOrIncorrect
                ? showIncorrect
                : undefined
            }
            isSelected={hasAnswered && isFreeAnswer}
            onClick={() => {
              let newAnswerRefs = selectedAnswers;
              if (option.itemId && !hasAnswered) {
                newAnswerRefs = selectedAnswers.includes(option.itemId)
                  ? selectedAnswers.filter(id => id !== option.itemId)
                  : [...selectedAnswers, option.itemId];
                setComponentMultiAnswer({
                  sectionId,
                  componentId: id,
                  answers: newAnswerRefs,
                  checkAnswerData,
                });
              }
              onSelectOption?.(newAnswerRefs);
            }}
          />
        );
      })}
    </MultipleChoiceItems>
  );
};

export default MultipleChoiceViewList;
